import { memo, type CSSProperties } from 'react';
import type { WidgetTemplate } from './catalog.js';

/**
 * The no-data preview of a template: a wireframe of the widget that fills in with the chart palette
 * when its card is hovered or selected. Colours come from `--c1`…`--c5`, which `TemplateCard` sets
 * (muted at rest, full palette on hover), so the wireframes hold no colour of their own except
 * neutral greys for stand-in text.
 */

const STUB = 'rgba(0, 0, 0, 0.09)';
const STUB_STRONG = 'rgba(0, 0, 0, 0.15)';
const TRACK = 'rgba(0, 0, 0, 0.06)';
const OUTLINE = 'rgba(0, 0, 0, 0.12)';
const PAPER = '#fff';

const fillOf = (n: number): CSSProperties => ({ fill: `var(--c${n})` });
const strokeOf = (n: number): CSSProperties => ({ stroke: `var(--c${n})` });

function Stub({
  x,
  y,
  w,
  h = 6,
  color = STUB,
}: {
  x: number;
  y: number;
  w: number;
  h?: number;
  color?: string;
}): React.ReactElement {
  return <rect x={x} y={y} width={w} height={h} rx={h / 2} fill={color} />;
}

// ─── Bars ─────────────────────────────────────────────────────────────────────

const BAR_VALUES = [0.42, 0.81, 0.365, 0.682];

function Bar(): React.ReactElement {
  return (
    <>
      {BAR_VALUES.map((v, i) => (
        <g key={`bar-${v}`}>
          <Stub x={22} y={23 + i * 30} w={38} />
          <rect x={70} y={14 + i * 30} width={v * 220} height={24} rx={2} style={fillOf(i + 1)} />
        </g>
      ))}
    </>
  );
}

const STACKED_ROWS = [
  [46, 36, 26],
  [62, 44, 30],
  [30, 40, 22],
  [52, 38, 34],
];

function StackedBar(): React.ReactElement {
  return (
    <>
      {STACKED_ROWS.map((row, i) => (
        <g key={`stacked-${row.join('-')}`}>
          <Stub x={22} y={23 + i * 30} w={38} />
          {row.map((v, k) => (
            <rect
              key={`seg-${k + 1}`}
              x={70 + row.slice(0, k).reduce((sum, n) => sum + n, 0)}
              y={14 + i * 30}
              width={v}
              height={24}
              style={fillOf(k + 1)}
            />
          ))}
        </g>
      ))}
    </>
  );
}

const CLUSTERED_GROUPS = [
  [7, 36, 50, 7],
  [19, 28, 31, 24],
];

function ClusteredBar(): React.ReactElement {
  return (
    <>
      {CLUSTERED_GROUPS.map((group, j) => (
        <g key={`group-${group.join('-')}`}>
          <Stub x={12} y={33 + j * 66} w={30} />
          {group.map((v, i) => (
            <rect key={`bar-${i + 1}`} x={50} y={12 + j * 66 + i * 14} width={v * 3.4} height={12} style={fillOf(i + 1)} />
          ))}
        </g>
      ))}
    </>
  );
}

const MULTI_STACKED_COLUMNS = [
  [34, 26, 18],
  [46, 30, 22],
  [28, 22, 16],
  [52, 34, 20],
  [40, 28, 24],
  [58, 30, 18],
];

function MultiStackedBar(): React.ReactElement {
  return (
    <>
      <line x1={30} y1={132} x2={240} y2={132} stroke={OUTLINE} />
      {MULTI_STACKED_COLUMNS.map((column, i) => {
        const x = 40 + i * 34;
        return (
          <g key={`column-${column.join('-')}-${x}`}>
            {column.map((v, k) => (
              <rect
                key={`seg-${k + 1}`}
                x={x}
                y={132 - column.slice(0, k + 1).reduce((sum, n) => sum + n, 0)}
                width={22}
                height={v}
                style={fillOf(k + 1)}
              />
            ))}
            <Stub x={x + 3} y={138} w={16} h={5} />
          </g>
        );
      })}
    </>
  );
}

// ─── Pie ──────────────────────────────────────────────────────────────────────

const PIE_SHARES = [13, 21, 17, 20, 29];
const PIE_CENTER = { x: 130, y: 75 };
const PIE_OUTER = 52;
const PIE_INNER = 28;

const pointAt = (angle: number, radius: number): string =>
  `${PIE_CENTER.x + radius * Math.sin(angle)} ${PIE_CENTER.y - radius * Math.cos(angle)}`;

/** Donut slice paths, one per share, computed once. */
const PIE_SLICES: { id: string; d: string; color: number }[] = (() => {
  let start = 0;
  return PIE_SHARES.map((share, i) => {
    const end = start + (share / 100) * Math.PI * 2;
    const d =
      `M${pointAt(start, PIE_OUTER)} A${PIE_OUTER} ${PIE_OUTER} 0 0 1 ${pointAt(end, PIE_OUTER)} ` +
      `L${pointAt(end, PIE_INNER)} A${PIE_INNER} ${PIE_INNER} 0 0 0 ${pointAt(start, PIE_INNER)}Z`;
    start = end;
    return { id: `slice-${share}`, d, color: i + 1 };
  });
})();

function Pie(): React.ReactElement {
  return (
    <>
      {PIE_SLICES.map((slice) => (
        <path key={slice.id} d={slice.d} stroke={PAPER} strokeWidth={1.5} style={fillOf(slice.color)} />
      ))}
    </>
  );
}

// ─── Lines ────────────────────────────────────────────────────────────────────

const GRID_LINES = [30, 60, 90, 120];
const LINE_POINTS = [0, 1, 2, 3, 4, 5, 6, 7];

const linePoint = (i: number, series: number): { x: number; y: number } => ({
  x: 30 + i * 28,
  y: 100 - Math.abs(Math.sin(i * 0.8 + series * 1.3)) * 60 - series * 8,
});

function Lines({ series }: { series: number }): React.ReactElement {
  return (
    <>
      {GRID_LINES.map((y) => (
        <line key={`grid-${y}`} x1={24} y1={y} x2={236} y2={y} stroke={OUTLINE} strokeDasharray="3 3" />
      ))}
      {Array.from({ length: series }, (_, k) => {
        const points = LINE_POINTS.map((i) => linePoint(i, k));
        const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x} ${p.y}`).join(' ');
        return (
          <g key={`series-${k + 1}`}>
            <path d={path} strokeWidth={2.5} fill="none" strokeLinejoin="round" style={strokeOf(k + 1)} />
            {series === 1 &&
              points.map((p) => (
                <circle key={`dot-${p.x}`} cx={p.x} cy={p.y} r={3} fill={PAPER} strokeWidth={2} style={strokeOf(1)} />
              ))}
          </g>
        );
      })}
    </>
  );
}

// ─── Table, KPI and progress ──────────────────────────────────────────────────

const TABLE_ROWS = [
  { label: 80, value: 40 },
  { label: 62, value: 50 },
  { label: 72, value: 30 },
  { label: 50, value: 25 },
];

function DataTable(): React.ReactElement {
  return (
    <>
      <rect x={20} y={14} width={220} height={122} rx={4} fill="none" stroke={OUTLINE} />
      <rect x={20} y={14} width={220} height={26} rx={4} style={fillOf(4)} opacity={0.55} />
      <line x1={130} y1={14} x2={130} y2={136} stroke={OUTLINE} />
      {TABLE_ROWS.map((row, i) => (
        <g key={`row-${row.label}`}>
          <line x1={20} y1={40 + i * 24} x2={240} y2={40 + i * 24} stroke={OUTLINE} />
          <Stub x={30} y={49 + i * 24} w={row.label} color={STUB_STRONG} />
          <Stub x={140} y={49 + i * 24} w={row.value} />
        </g>
      ))}
    </>
  );
}

function Kpi(): React.ReactElement {
  return (
    <>
      <text x={130} y={78} fontSize={34} fontWeight={600} textAnchor="middle" style={fillOf(2)}>
        0,000
      </text>
      <Stub x={75} y={96} w={110} />
    </>
  );
}

function ProgressBar(): React.ReactElement {
  return (
    <>
      <Stub x={105} y={30} w={50} h={16} color={STUB_STRONG} />
      <rect x={20} y={62} width={220} height={14} rx={7} fill={TRACK} />
      <rect x={20} y={62} width={150} height={14} rx={7} style={fillOf(2)} />
      <Stub x={20} y={92} w={120} />
    </>
  );
}

function SemiCircleProgress(): React.ReactElement {
  return (
    <>
      <path d="M70 105 A60 60 0 0 1 190 105" stroke={TRACK} strokeWidth={14} fill="none" />
      <path d="M70 105 A60 60 0 0 1 178 69" strokeWidth={14} fill="none" style={strokeOf(1)} />
      <Stub x={108} y={84} w={44} h={14} color={STUB_STRONG} />
      <Stub x={100} y={118} w={60} h={5} />
    </>
  );
}

// ─── Content ──────────────────────────────────────────────────────────────────

const TEXT_LINES = [200, 186, 200, 140];

function RichText(): React.ReactElement {
  return (
    <>
      <Stub x={30} y={26} w={130} h={12} color={STUB_STRONG} />
      {TEXT_LINES.map((w, i) => (
        <Stub key={`line-${w}-${i + 1}`} x={30} y={54 + i * 18} w={w} />
      ))}
      <rect x={30} y={54 + 3 * 18 + 16} width={2} height={16} style={fillOf(1)} />
    </>
  );
}

const QUOTE_LINES = [180, 170, 120];

function Quote(): React.ReactElement {
  return (
    <>
      <rect x={22} y={10} width={216} height={130} rx={4} fill={PAPER} stroke={OUTLINE} />
      <rect x={22} y={10} width={8} height={130} style={fillOf(2)} />
      <text x={42} y={40} fontSize={30} fontFamily="Georgia, serif" fill={STUB_STRONG}>
        &rdquo;
      </text>
      {QUOTE_LINES.map((w, i) => (
        <Stub key={`line-${w}`} x={44} y={52 + i * 14} w={w} h={7} />
      ))}
      <rect x={44} y={96} width={180} height={22} rx={3} fill="none" stroke={OUTLINE} />
      <Stub x={52} y={104} w={100} h={5} />
      <rect x={44} y={124} width={64} height={12} rx={4} fill="none" stroke={OUTLINE} />
    </>
  );
}

function Media(): React.ReactElement {
  return (
    <>
      <rect x={40} y={6} width={180} height={101} rx={4} fill={TRACK} />
      <circle cx={130} cy={56} r={17} style={fillOf(1)} />
      <polygon points="124,47 124,65 139,56" fill={PAPER} />
      <rect x={50} y={96} width={160} height={3} rx={1.5} fill={STUB} />
      <rect x={50} y={96} width={30} height={3} rx={1.5} style={fillOf(1)} />
      <Stub x={40} y={116} w={150} />
      <rect x={40} y={130} width={44} height={12} rx={6} style={fillOf(5)} opacity={0.55} />
      <rect x={90} y={130} width={54} height={12} rx={6} style={fillOf(2)} opacity={0.55} />
    </>
  );
}

const SECTION_SLOTS = [30, 136];

function SectionContainer(): React.ReactElement {
  return (
    <>
      <rect x={16} y={10} width={228} height={130} rx={8} fill="none" stroke={OUTLINE} strokeDasharray="5 4" />
      <Stub x={30} y={24} w={110} h={10} color={STUB_STRONG} />
      {SECTION_SLOTS.map((x) => (
        <g key={`slot-${x}`}>
          <rect x={x} y={46} width={96} height={80} rx={6} fill="none" stroke={OUTLINE} strokeDasharray="4 3" />
          <circle cx={x + 48} cy={86} r={11} fill="none" strokeWidth={1.6} style={strokeOf(3)} />
          <line x1={x + 43} y1={86} x2={x + 53} y2={86} strokeWidth={1.6} strokeLinecap="round" style={strokeOf(3)} />
          <line x1={x + 48} y1={81} x2={x + 48} y2={91} strokeWidth={1.6} strokeLinecap="round" style={strokeOf(3)} />
        </g>
      ))}
    </>
  );
}

// ─── Lookup ───────────────────────────────────────────────────────────────────

/** The wireframe for a template, keyed by its chart type or, for non-charts, its template id. */
function renderWireframe(template: WidgetTemplate): React.ReactElement | null {
  switch (template.chartType ?? template.id) {
    case 'bar':
      return <Bar />;
    case 'stacked-bar':
      return <StackedBar />;
    case 'multiple-clustered-bar':
      return <ClusteredBar />;
    case 'multiple-stacked-bar':
      return <MultiStackedBar />;
    case 'pie':
      return <Pie />;
    case 'line':
      return <Lines series={1} />;
    case 'multiple-line':
      return <Lines series={3} />;
    case 'data-table':
      return <DataTable />;
    case 'kpi-widget':
      return <Kpi />;
    case 'progress-bar-widget':
      return <ProgressBar />;
    case 'semi-circle-progress-widget':
      return <SemiCircleProgress />;
    case 'text':
      return <RichText />;
    case 'quote':
      return <Quote />;
    case 'media':
      return <Media />;
    case 'section':
      return <SectionContainer />;
    default:
      return null;
  }
}

export const Wireframe = memo(function Wireframe({ template }: { template: WidgetTemplate }): React.ReactElement {
  return (
    <svg viewBox="0 0 260 150" width="100%" height={150} role="img" aria-label={`${template.label} template`}>
      {renderWireframe(template)}
    </svg>
  );
});
